import {
  MARKET_COUNTRY_OPTIONS,
  restoredProductLanguage,
} from "../onboarding/experience-catalog.mjs";

const LAUNCH_COUNTRIES = new Set(MARKET_COUNTRY_OPTIONS.map((entry) => entry.code));
export const CURRENTLY_UNAVAILABLE_NEWS_SOURCE_IDS = Object.freeze([
  "local-news-gemini",
  "category-global-de-politics",
  "category-global-jp-national",
  "category-global-jp-business",
  "category-global-jp-sports",
  "category-global-jp-politics",
]);
const CURRENTLY_UNAVAILABLE_NEWS_SOURCES = new Set(CURRENTLY_UNAVAILABLE_NEWS_SOURCE_IDS);
const RETIRED_NEWS_PUBLISHER_SOURCE = new Map([
  ["ca-global-news-money", "ca-financial-post-business"],
]);

const RETIRED_LEGACY_NEWS_CATEGORY_KIND = new Map([
  ["category-india", "national"],
  ["category-world", "world"],
  ["category-business-india", "business"],
  ["category-business-world", "business"],
  ["category-ai", "ai"],
  ["category-technology", "technology"],
  ["category-sports", "sports"],
  ["category-politics", "politics"],
  ["category-us-national", "national"],
  ["category-ca-national", "national"],
  ["category-uk-national", "national"],
  ["category-au-national", "national"],
  ["category-de-national", "national"],
  ["category-fr-national", "national"],
  ["category-jp-national", "national"],
]);
export const RETIRED_LEGACY_NEWS_CATEGORY_SOURCE_IDS = Object.freeze([
  ...RETIRED_LEGACY_NEWS_CATEGORY_KIND.keys(),
]);

function launchCountry(value) {
  const country = String(value || "")
    .trim()
    .toUpperCase();
  return LAUNCH_COUNTRIES.has(country) ? country : null;
}

export function normalizeMarketCountry(value, fallback = "US") {
  return launchCountry(value) || launchCountry(fallback) || "US";
}

function marketExperience(country) {
  return (
    MARKET_COUNTRY_OPTIONS.find((entry) => entry.code === country) ||
    MARKET_COUNTRY_OPTIONS.find((entry) => entry.code === "US") ||
    MARKET_COUNTRY_OPTIONS[0]
  );
}

export function defaultNewsSourceForMarketCountry(country) {
  return marketExperience(normalizeMarketCountry(country))?.defaultNewsSource || "";
}

export function migrateUnavailableNewsSource(sourceId, country) {
  const source = typeof sourceId === "string" ? sourceId.trim() : "";
  const normalizedSource = source.toLowerCase();
  const marketCountry = normalizeMarketCountry(country);
  if (!source || CURRENTLY_UNAVAILABLE_NEWS_SOURCES.has(normalizedSource)) {
    return defaultNewsSourceForMarketCountry(marketCountry);
  }
  const retiredCategoryKind = RETIRED_LEGACY_NEWS_CATEGORY_KIND.get(normalizedSource);
  if (retiredCategoryKind) {
    const canonicalSource = `category-global-${marketCountry.toLowerCase()}-${retiredCategoryKind}`;
    return CURRENTLY_UNAVAILABLE_NEWS_SOURCES.has(canonicalSource)
      ? defaultNewsSourceForMarketCountry(marketCountry)
      : canonicalSource;
  }
  return RETIRED_NEWS_PUBLISHER_SOURCE.get(normalizedSource) || source;
}

export function normalizeMarketLanguage(value, country) {
  const experience = marketExperience(normalizeMarketCountry(country));
  const supportedLanguages = experience?.languages || ["en"];
  const language = restoredProductLanguage(value);
  return language && supportedLanguages.includes(language)
    ? language
    : supportedLanguages[0] || "en";
}

export function marketRegionPreferenceTransition(
  preferences,
  targetCountryRaw,
  previousCountryRaw = preferences?.country,
) {
  const current = preferences && typeof preferences === "object" ? preferences : {};
  const previousCountry = normalizeMarketCountry(previousCountryRaw);
  const targetCountry = normalizeMarketCountry(targetCountryRaw, previousCountry);
  const countryChanged = previousCountry !== targetCountry;
  const lang = normalizeMarketLanguage(current.lang, targetCountry);
  const currentNewsSource = migrateUnavailableNewsSource(current.newsSource, targetCountry);
  const currentPrimary = migrateUnavailableNewsSource(
    current.newsBriefPrimarySource,
    targetCountry,
  );
  const rawSecondary =
    typeof current.newsBriefSecondarySource === "string"
      ? current.newsBriefSecondarySource.trim()
      : "";
  const currentSecondary = rawSecondary
    ? migrateUnavailableNewsSource(rawSecondary, targetCountry)
    : "";
  const defaultNewsSource = defaultNewsSourceForMarketCountry(targetCountry) || currentNewsSource;
  return {
    ...current,
    country: targetCountry,
    lang,
    newsSource: countryChanged ? defaultNewsSource : currentNewsSource,
    newsBriefPrimarySource: countryChanged ? defaultNewsSource : currentPrimary,
    // Secondary editions are optional. Clearing is safer than carrying a
    // country-specific feed into the next market or duplicating the new primary.
    newsBriefSecondarySource: countryChanged ? "" : currentSecondary,
  };
}
